<template>
  <HeaderBar />

  <section class="carvia-container">
    <div class="flex justify-between">
      <Input v-model="search" :placeholder="t('search')" />
      <div class="flex gap-4">
        <CVButton
          v-if="$can('payouts', 'AgentProvision')"
          @click.prevent="downloadAgentProvisions"
          >{{ t('downloadAgentProvisions') }}</CVButton
        >
        <CVButton
          v-if="$can('manage', 'PartnerSubType')"
          @click.prevent="router.push({ name: 'partnerSubTypeList' })"
          >{{ t('partnerSubTypes') }}</CVButton
        >
        <CVButton
          v-if="$can('manage', 'User')"
          @click.prevent="router.push({ name: 'user', params: { id: 'new' } })"
          >{{ t('createNewUser') }}</CVButton
        >
      </div>
    </div>

    <div class="mb-4 mt-6">
      <Tabs v-model="activeTab" :tabs="tabs" />

      <div class="grid gap-4 md:grid-cols-3">
        <Select
          v-if="activeTab === Tab.AGENTS"
          v-model="selectedPartnerSubTypeId"
          variant="ghost"
          :options="agentSubTypeOptions"
          empty-value-to="undefined"
          class="mb-4 mt-6"
        />
      </div>
    </div>

    <div>
      <div v-if="users" class="flex flex-col space-y-2">
        <div v-for="user in users" :key="user.id">
          <UserListItem :user="user" />
        </div>
      </div>

      <EntityListExceptions
        :status="usersStatus"
        :error="usersError"
        :items-count="users.length"
        :no-items-text="t('noUsersFound')"
      />

      <div v-if="hasNextPage" class="flex justify-center pt-8">
        <CVButton outline :is-loading="isFetchingNextPage" @click="() => fetchNextPage()">
          {{ t('loadMore') }}
        </CVButton>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import { useI18n } from 'vue-i18n';
import router from '@/router';
import { computed, ref, watchEffect } from 'vue';
import { usePartnerSubTypes, useUsersWithQuery } from '@/queries/use-users';
import Tabs from '@/components/Tabs.vue';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import type { FindAllUsersQueryDto } from '@/entities/auth/user.entity';
import { UserRole } from '@/entities/auth/user-role.enum';
import { FindAllUsersSort } from '@/entities/auth/find-all-users-sort.enum';
import { Order } from '@/entities/pagination/order.enum';
import Input from '@/components/Input.vue';
import UserListItem from './UserListItem.vue';
import { useUiStore } from '@/stores/ui.store';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import { agentProvisionService } from '@/api/agent-provision.service';
import { downloadBlob } from '@/utils/download-file';
import { refDebounced } from '@vueuse/core';
import Select from '@/components/Select.vue';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import { useLocalizedFields } from '@/hooks/use-localized-fields';

enum Tab {
  ALL = 'ALL',
  STAFF = 'STAFF',
  AGENTS = 'AGENTS',
  CUSTOMERS = 'CUSTOMERS',
  COMPANIES = 'COMPANIES',
  FRANCHISE_BROS = 'FRANCHISE_BROS',
}

const { t } = useI18n();
const uiStore = useUiStore();
const { getLocalizedField } = useLocalizedFields();

const activeTab = ref(Tab.STAFF);
const selectedPartnerSubTypeId = useRouteQueryRef('partnerSubTypeId', '', 'string');

const search = useRouteQueryRef('search', '', 'string');
const searchQuery = refDebounced(search, 500);

const query = computed<FindAllUsersQueryDto>(() => {
  let activeQuery: FindAllUsersQueryDto = {};
  switch (activeTab.value) {
    case Tab.STAFF:
      activeQuery = {
        role: [UserRole.ADMIN, UserRole.HUB_MANAGER, UserRole.HUB_AGENT, UserRole.OPERATOR],
      };
      break;
    case Tab.AGENTS:
      activeQuery = {
        role: [UserRole.AGENCY],
        partnerSubTypeId: selectedPartnerSubTypeId.value || undefined,
      };
      break;
    case Tab.CUSTOMERS:
      activeQuery = {
        role: [UserRole.CUSTOMER],
      };
      break;
    case Tab.COMPANIES:
      activeQuery = {
        role: [UserRole.COMPANY],
      };
      break;
    case Tab.FRANCHISE_BROS:
      activeQuery = {
        role: [UserRole.FRANCHISE_AGENT],
      };
      break;
  }
  return {
    ...activeQuery,
    query: searchQuery.value,
    limit: 10,
    sort: FindAllUsersSort.FIRST_NAME,
    order: Order.ASC,
  };
});

const {
  data: usersData,
  status: usersStatus,
  error: usersError,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
} = useUsersWithQuery(query);
const users = useFlattenPaginatedData(usersData);
const { data: partnerSubTypes } = usePartnerSubTypes();

const tabs = computed(() => [
  {
    title: t('staff'),
    id: Tab.STAFF,
  },
  {
    title: t('franchiseBros'),
    id: Tab.FRANCHISE_BROS,
  },
  {
    title: t('agents'),
    id: Tab.AGENTS,
  },
  {
    title: t('customers'),
    id: Tab.CUSTOMERS,
  },
  {
    title: t('companies'),
    id: Tab.COMPANIES,
  },
  {
    title: t('all'),
    id: Tab.ALL,
  },
]);

const agentSubTypeOptions = computed(() => [
  {
    label: t('allAgencies'),
    value: '',
  },
  ...((partnerSubTypes.value ?? [])
    .filter((partnerSubType) => partnerSubType.role === UserRole.AGENCY)
    .map((partnerSubType) => ({
      label: getLocalizedField(partnerSubType.name),
      value: partnerSubType.id,
    })) || []),
]);

const downloadAgentProvisions = async () => {
  const res = await agentProvisionService.getPayouts();
  await downloadBlob(new Blob([res], { type: 'text/csv;charset=utf-8' }), 'agent-provisions.csv');
};

watchEffect(() => {
  uiStore.setHeaderTitle(t('users'));
});
</script>

<i18n lang="json">
{
  "en": {
    "search": "Search",
    "partnerSubTypes": "Partner Sub Types",
    "createNewUser": "+ Create new user",
    "staff": "Staff",
    "franchiseBros": "Franchise Bros",
    "agents": "Agencies",
    "customers": "Customers",
    "companies": "Business Accounts",
    "all": "All",
    "allAgencies": "All Agencies",
    "users": "Users",
    "noUsersFound": "No users found",
    "downloadAgentProvisions": "Download Agent Provisions"
  },
  "de": {
    "search": "Suche",
    "partnerSubTypes": "Partner-Subtypen",
    "createNewUser": "+ Neuen User erstellen",
    "staff": "Mitarbeiter",
    "franchiseBros": "Franchise Bros",
    "agents": "Vermittler",
    "customers": "Kunden",
    "companies": "Businesskunden",
    "all": "Alle",
    "allAgencies": "Alle Vermittler",
    "users": "Benutzer",
    "noUsersFound": "Keine Benutzer gefunden",
    "downloadAgentProvisions": "Agent Provisionen herunterladen"
  },
  "es": {
    "search": "Buscar",
    "partnerSubTypes": "Subtipos de partner",
    "createNewUser": "+ Crear usuario",
    "staff": "Personal",
    "franchiseBros": "Franchise Bros",
    "agents": "Agencias",
    "customers": "Clientes",
    "companies": "Cuentas empresa",
    "all": "Todos",
    "allAgencies": "Todas las agencias",
    "users": "Usuarios",
    "noUsersFound": "No se encontraron usuarios",
    "downloadAgentProvisions": "Descargar comisiones de agentes"
  }
}
</i18n>
